import moment from "moment";
import { theme } from "nectar-ui";
import { ApexOptions } from "apexcharts";

import {
  CHART_COLORS_PALLETE,
  DASHBOARD_CHART_TYPES,
  DASHBOARD_RANGE_TYPES_DICTIONARY,
  DASHBOARD_RANGE_TYPE_KEYS,
  DATE_RANGE_MAP,
  DASHBOARD_CHART_VISUALIZATION_TYPES,
  DashboardChartVisualizationKeys,
} from "@utils/constants/dashboard/dashboardModuleConstants";
import {
  apexChartInvertedSeriesClickHandler,
  getXAxisTooltipFormatterBasedOnRange,
} from "@utils/helpers/dashboardHelpers";

export const MAXIMUM_RESPONSIBLE_NUMBER = 8;
export const MINIMUM_RESPONSIBLE_NUMBER = 1;

type GetFromAndToDate = {
  (
    dateRange: { fromDate: moment.Moment; toDate: moment.Moment } | string,
    formatTemplate?: string
  ): { fromDate: string; toDate: string };
};

export const getFromAndToDate: GetFromAndToDate = (dateRange, formatTemplate = "YYYY-MM-DD") => {
  if (typeof dateRange !== "string") {
    return {
      fromDate: dateRange.fromDate.format(formatTemplate),
      toDate: dateRange.toDate.format(formatTemplate),
    };
  }
  return DASHBOARD_RANGE_TYPES_DICTIONARY[dateRange as DASHBOARD_RANGE_TYPE_KEYS].range;
};

const getBaseChartOptions = (): ApexOptions => ({
  chart: {
    id: DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_RESPONSIBLE.chart,
    fontFamily: theme.nectar.fontStack.default,
    toolbar: { show: false },
    zoom: { enabled: false },
    events: { legendClick: apexChartInvertedSeriesClickHandler },
  },
  plotOptions: {},
  colors: CHART_COLORS_PALLETE,
  dataLabels: { enabled: false },
  legend: {
    horizontalAlign: "center",
    onItemClick: { toggleDataSeries: false },
    markers: { width: 16, height: 16, radius: 2 },
  },
  stroke: {
    width: 3,
    curve: "straight",
  },
  title: {
    text: "",
    align: "left",
  },
  tooltip: {
    x: { formatter: getXAxisTooltipFormatterBasedOnRange },
    items: {},
  },
  markers: {
    size: 0,
    hover: {
      sizeOffset: 6,
    },
  },
  grid: {
    borderColor: "#DEE2E6",
    position: "back",
  },
  responsive: [
    {
      breakpoint: 768,
      options: { xaxis: { tickAmount: 3 } },
    },
  ],
});

const getLineChartOptions = (
  xAxisCategories: string[],
  groups: { title: string; cols: number }[],
  rangeFilter: string
): ApexOptions => {
  const xAxisLabelFormatter = (date: moment.MomentInput) => {
    const [dayString, monthString] = moment(date, "YYYY-MM-DD").format("D MMM").split(" ");
    const formattedMonth = monthString.charAt(0).toUpperCase() + monthString.substring(1);
    if (rangeFilter === DATE_RANGE_MAP.LAST_YEAR) return formattedMonth;
    return `${dayString} ${formattedMonth}`;
  };

  return {
    chart: { type: "line" },
    stroke: { width: 3, curve: "straight" },
    markers: { size: 0, hover: { sizeOffset: 6 } },
    grid: {
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      categories: xAxisCategories,
      tickAmount: 15,
      tickPlacement: "between",
      labels: {
        hideOverlappingLabels: false,
        rotate: 0,
        style: {
          fontSize: "14px",
          fontWeight: 300,
          colors: theme.nectar.colors.gray500,
        },
        formatter: xAxisLabelFormatter,
      },
      group: { groups, style: { fontSize: "14px" } },
    },
    yaxis: {
      labels: {
        formatter: (value) => Math.round(+value).toString(),
        style: {
          fontSize: "14px",
          fontWeight: 500,
          colors: theme.nectar.colors.gray500,
        },
      },
    },
  };
};

const getVerticalBarChartOptions = (
  xAxisCategories: string[],
  groups: { title: string; cols: number }[],
  rangeFilter: string
): ApexOptions => {
  const lineChartOptions = getLineChartOptions(xAxisCategories, groups, rangeFilter);

  return {
    ...lineChartOptions,
    chart: { type: "bar" },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "85%",
        borderRadius: 4,
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    markers: {},
  };
};

const getHorizontalBarChartOptions = (
  xAxisCategories: string[],
  rangeFilter: string
): ApexOptions => {
  const yAxisLabelFormatter = (date: moment.MomentInput) => {
    const [dayString, monthString] = moment(date, "YYYY-MM-DD").format("D MMM").split(" ");
    const formattedMonth = monthString.charAt(0).toUpperCase() + monthString.substring(1);
    if (rangeFilter === DATE_RANGE_MAP.LAST_YEAR) return formattedMonth;
    return `${dayString} ${formattedMonth}`;
  };

  return {
    chart: { type: "bar" },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    grid: {
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: xAxisCategories,
      labels: {
        formatter: (value) => Math.round(+value).toString(),
        style: {
          fontSize: "14px",
          fontWeight: 500,
          colors: theme.nectar.colors.gray500,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: yAxisLabelFormatter,
        style: {
          fontSize: "14px",
          fontWeight: 300,
          colors: theme.nectar.colors.gray500,
        },
      },
    },
    markers: {},
  };
};

export const getChartOptions = (
  xAxisCategories: string[],
  groups: { title: string; cols: number }[],
  rangeFilter: string,
  chartType: DashboardChartVisualizationKeys
): ApexOptions => {
  const baseOptions = getBaseChartOptions();
  let specificOptions: ApexOptions = {};

  switch (chartType) {
    case DASHBOARD_CHART_VISUALIZATION_TYPES.LINE.value:
      specificOptions = getLineChartOptions(xAxisCategories, groups, rangeFilter);
      break;
    case DASHBOARD_CHART_VISUALIZATION_TYPES.VERTICAL_BAR.value:
      specificOptions = getVerticalBarChartOptions(xAxisCategories, groups, rangeFilter);
      break;
    case DASHBOARD_CHART_VISUALIZATION_TYPES.HORIZONTAL_BAR.value:
      specificOptions = getHorizontalBarChartOptions(xAxisCategories, rangeFilter);
      break;
    default:
      specificOptions = {};
  }

  return {
    ...baseOptions,
    ...specificOptions,
    chart: { ...baseOptions.chart, ...specificOptions.chart },
  };
};
